import { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { LuSearch } from "react-icons/lu";
import { usePathBatchCountsQuery, useV2PathListQuery } from "features/Path/hooks";
import { Path, PathDetails } from "features/Path/models";
import IconTile from "features/Path/Components/PathsList/IconTile";

function formatPathStepCounts(c: PathDetails | undefined): string {
  if (!c) return "0 steps";
  const parts = [
    c.taskTemplatesCount > 0 && `${c.taskTemplatesCount} ${c.taskTemplatesCount === 1 ? "task" : "tasks"}`,
    c.emailTemplatesCount > 0 && `${c.emailTemplatesCount} ${c.emailTemplatesCount === 1 ? "email" : "emails"}`,
    c.messageTemplatesCount > 0 && `${c.messageTemplatesCount} ${c.messageTemplatesCount === 1 ? "message" : "messages"}`,
    c.meetingTemplatesCount > 0 && `${c.meetingTemplatesCount} ${c.meetingTemplatesCount === 1 ? "meeting" : "meetings"}`,
    c.checkpointTemplatesCount > 0 && `${c.checkpointTemplatesCount} ${c.checkpointTemplatesCount === 1 ? "checkpoint" : "checkpoints"}`,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : `${c.stepsCount ?? 0} steps`;
}

function summarizePaths(ids: string[], pathById: Map<string, Path>, countsMap: Record<string, PathDetails>) {
  const paths = ids.map((id) => pathById.get(id)).filter(Boolean) as Path[];
  const totals = paths.reduce(
    (acc, p) => {
      const c = countsMap[p.id];
      acc.tasks += c?.taskTemplatesCount || 0;
      acc.emails += c?.emailTemplatesCount || 0;
      acc.messages += c?.messageTemplatesCount || 0;
      acc.meetings += c?.meetingTemplatesCount || 0;
      acc.checkpoints += c?.checkpointTemplatesCount || 0;
      return acc;
    },
    { tasks: 0, emails: 0, messages: 0, meetings: 0, checkpoints: 0 },
  );
  return { names: paths.map((p) => p.name), ...totals };
}

interface AddPathsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPaths: Path[];
  newHireName?: string;
  onSave: (pathIds: string[]) => void;
  isSaving: boolean;
}

function AddPathsModal({
  isOpen, onClose, currentPaths, newHireName, onSave, isSaving,
}: AddPathsModalProps) {
  const [searchText, setSearchText] = useState("");
  // Initialize directly from currentPaths — the parent passes key={modalKey} so this
  // component remounts fresh each time the modal opens, making the initializer correct.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(currentPaths.map((p) => p.id)),
  );
  const initialSelectedIds = useMemo(
    () => new Set(currentPaths.map((p) => p.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const searchRef = useRef<HTMLInputElement>(null);

  const { data: allPaths, isLoading } = useV2PathListQuery("");
  const pathIds = useMemo(() => (allPaths ?? []).map((p) => p.id), [allPaths]);
  const { data: countsMap = {} } = usePathBatchCountsQuery(pathIds);

  const pathById = useMemo(() => {
    const map = new Map<string, Path>();
    (allPaths ?? currentPaths).forEach((p) => map.set(p.id, p));
    return map;
  }, [allPaths, currentPaths]);

  // Chips: currently selected paths in stable insertion order
  const selectedPaths = useMemo(
    () => [...selectedIds].map((id) => pathById.get(id)).filter(Boolean) as Path[],
    [selectedIds, pathById],
  );

  // List: unselected paths filtered by search
  const listPaths = useMemo(() => {
    if (!allPaths) return [];
    const query = searchText.trim().toLowerCase();
    return allPaths.filter(
      (p) => !selectedIds.has(p.id) && (!query || p.name.toLowerCase().includes(query)),
    );
  }, [allPaths, selectedIds, searchText]);

  // Delta: paths being newly added (not already on the journey)
  const newPathIds = useMemo(
    () => [...selectedIds].filter((id) => !initialSelectedIds.has(id)),
    [selectedIds, initialSelectedIds],
  );
  const removedPathIds = useMemo(
    () => [...initialSelectedIds].filter((id) => !selectedIds.has(id)),
    [selectedIds, initialSelectedIds],
  );
  const hasChanges = newPathIds.length > 0 || removedPathIds.length > 0;

  const newPathsSummary = useMemo(
    () => summarizePaths(newPathIds, pathById, countsMap),
    [countsMap, newPathIds, pathById],
  );
  const removedPathsSummary = useMemo(
    () => summarizePaths(removedPathIds, pathById, countsMap),
    [countsMap, removedPathIds, pathById],
  );

  function handleSelect(pathId: string) {
    setSelectedIds((prev) => new Set([...prev, pathId]));
  }

  function handleDeselect(pathId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(pathId);
      return next;
    });
  }

  function handleSave() {
    onSave(Array.from(selectedIds));
  }

  function handleClose() {
    onClose();
  }

  const subtitle = newHireName
    ? `Choose the onboarding paths to attach to ${newHireName}'s journey.`
    : "Select the paths to include in this new hire's onboarding journey.";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      scrollBehavior="inside"
      initialFocusRef={searchRef}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb="1">
          <Text fontSize="lg" fontWeight="bold">Add paths</Text>
          <Text fontSize="sm" fontWeight="normal" color="fg.subtle" mt="1">
            {subtitle}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pt="3" pb="2">
          {/* Search */}
          <InputGroup mb={selectedPaths.length > 0 ? "2" : "3"}>
            <InputLeftElement pointerEvents="none">
              <Icon as={LuSearch} color="gray.400" />
            </InputLeftElement>
            <Input
              ref={searchRef}
              placeholder="Search paths..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              borderRadius="full"
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              _focus={{ bg: "white", borderColor: "blue.400" }}
            />
          </InputGroup>

          {/* Selected chips */}
          {selectedPaths.length > 0 && (
            <Wrap spacing="2" mb="3">
              {selectedPaths.map((path) => (
                <WrapItem key={path.id}>
                  <Tag
                    size="md"
                    borderRadius="full"
                    colorScheme="blue"
                    variant="subtle"
                    pl="1.5"
                  >
                    <Box mr="1.5" flexShrink={0}>
                      <IconTile icon={path.icon} color={path.iconColor} size={18} />
                    </Box>
                    <TagLabel fontSize="xs" fontWeight="semibold">{path.name}</TagLabel>
                    <TagCloseButton onClick={() => handleDeselect(path.id)} />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}

          {/* Unselected path list */}
          {isLoading ? (
            <Flex justify="center" align="center" py="10">
              <Spinner size="md" color="blue.500" />
            </Flex>
          ) : listPaths.length === 0 ? (
            <Flex justify="center" align="center" py="8">
              <Text fontSize="sm" color="fg.subtle">
                {searchText ? "No paths match your search" : "All paths selected"}
              </Text>
            </Flex>
          ) : (
            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
              {listPaths.map((path, i) => (
                <Flex
                  key={path.id}
                  align="center"
                  gap="3"
                  px="4"
                  py="3"
                  cursor="pointer"
                  bg="white"
                  borderTop={i > 0 ? "1px solid" : undefined}
                  borderColor="gray.100"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => handleSelect(path.id)}
                >
                  <IconTile icon={path.icon} color={path.iconColor} size={32} />
                  <Box flex="1" minWidth="0">
                    <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                      {path.name}
                    </Text>
                    <Text fontSize="xs" color="fg.subtle">
                      {formatPathStepCounts(countsMap[path.id])}
                    </Text>
                  </Box>
                </Flex>
              ))}
            </Box>
          )}

          {/* Summary — show when adding or removing paths */}
          {hasChanges && !isLoading && (
            <Box mt="3" border="1px dashed" borderColor="gray.300" borderRadius="md" overflow="hidden">
              {newPathIds.length > 0 && (
                <Box p="3" bg="green.50">
                  <Text fontSize="xs" fontWeight="bold" color="green.700" textTransform="uppercase" mb="2" letterSpacing="wider">
                    Adding to {newHireName ? `${newHireName}'s` : "this"} journey
                  </Text>
                  <Flex gap="4" align="baseline" flexWrap="wrap">
                    {newPathsSummary.tasks > 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{newPathsSummary.tasks}</Text>{" "}{newPathsSummary.tasks === 1 ? "task" : "tasks"}</Text>
                    )}
                    {newPathsSummary.emails > 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{newPathsSummary.emails}</Text>{" "}{newPathsSummary.emails === 1 ? "email" : "emails"}</Text>
                    )}
                    {newPathsSummary.messages > 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{newPathsSummary.messages}</Text>{" "}{newPathsSummary.messages === 1 ? "message" : "messages"}</Text>
                    )}
                    {newPathsSummary.meetings > 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{newPathsSummary.meetings}</Text>{" "}{newPathsSummary.meetings === 1 ? "meeting" : "meetings"}</Text>
                    )}
                    {newPathsSummary.checkpoints > 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{newPathsSummary.checkpoints}</Text>{" "}{newPathsSummary.checkpoints === 1 ? "checkpoint" : "checkpoints"}</Text>
                    )}
                    {newPathsSummary.tasks === 0 && newPathsSummary.emails === 0 && newPathsSummary.messages === 0 && newPathsSummary.meetings === 0 && newPathsSummary.checkpoints === 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{newPathIds.length}</Text>{" "}{newPathIds.length === 1 ? "path" : "paths"}</Text>
                    )}
                  </Flex>
                  <Text fontSize="xs" color="green.600" mt="2">{newPathsSummary.names.join(" · ")}</Text>
                </Box>
              )}
              {removedPathIds.length > 0 && (
                <Box p="3" bg="red.50" borderTop={newPathIds.length > 0 ? "1px dashed" : undefined} borderColor="gray.300">
                  <Text fontSize="xs" fontWeight="bold" color="red.600" textTransform="uppercase" mb="2" letterSpacing="wider">
                    Removing from {newHireName ? `${newHireName}'s` : "this"} journey
                  </Text>
                  <Flex gap="4" align="baseline" flexWrap="wrap">
                    {removedPathsSummary.tasks > 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{removedPathsSummary.tasks}</Text>{" "}{removedPathsSummary.tasks === 1 ? "task" : "tasks"}</Text>
                    )}
                    {removedPathsSummary.emails > 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{removedPathsSummary.emails}</Text>{" "}{removedPathsSummary.emails === 1 ? "email" : "emails"}</Text>
                    )}
                    {removedPathsSummary.messages > 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{removedPathsSummary.messages}</Text>{" "}{removedPathsSummary.messages === 1 ? "message" : "messages"}</Text>
                    )}
                    {removedPathsSummary.meetings > 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{removedPathsSummary.meetings}</Text>{" "}{removedPathsSummary.meetings === 1 ? "meeting" : "meetings"}</Text>
                    )}
                    {removedPathsSummary.checkpoints > 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{removedPathsSummary.checkpoints}</Text>{" "}{removedPathsSummary.checkpoints === 1 ? "checkpoint" : "checkpoints"}</Text>
                    )}
                    {removedPathsSummary.tasks === 0 && removedPathsSummary.emails === 0 && removedPathsSummary.messages === 0 && removedPathsSummary.meetings === 0 && removedPathsSummary.checkpoints === 0 && (
                      <Text fontSize="sm"><Text as="span" fontWeight="bold" fontSize="lg">{removedPathIds.length}</Text>{" "}{removedPathIds.length === 1 ? "path" : "paths"}</Text>
                    )}
                  </Flex>
                  <Text fontSize="xs" color="red.400" mt="2">{removedPathsSummary.names.join(" · ")}</Text>
                </Box>
              )}
            </Box>
          )}
        </ModalBody>

        <ModalFooter gap="3" pt="4">
          <Button variant="ghost" onClick={handleClose} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={isSaving || !hasChanges}
            borderRadius="full"
            px="6"
          >
            {hasChanges
              ? newPathIds.length > 0
                ? `Add ${newPathIds.length} ${newPathIds.length === 1 ? "path" : "paths"}`
                : "Save changes"
              : "No changes"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AddPathsModal;
